/**
 * Environment Configuration
 *
 * Centralized configuration for different deployment environments
 */

export type Environment =
  | 'development'
  | 'dev-remote'
  | 'staging'
  | 'production';

interface EnvironmentConfig {
  apiUrl: string | null;
  authUrl: string | null;
  authCookieName: string;
  authCookieOptions: {
    httpOnly: boolean;
    secure: boolean;
    sameSite: 'strict' | 'lax' | 'none';
    path: string;
    maxAge?: number;
  };
}

const isServerSide = typeof window === 'undefined';

const configs: Record<Environment, EnvironmentConfig> = {
  development: {
    apiUrl:
      process.env.API_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      'http://localhost:4000',
    authUrl: process.env.NEXT_PUBLIC_AUTH_URL || 'https://auth.vale.rip',
    authCookieName: 'vale-auth-token',
    authCookieOptions: {
      httpOnly: true,
      secure: false, // Allow http in development
      sameSite: 'lax',
      path: '/',
      maxAge: 60 * 60 * 24 * 7 // 7 days
    }
  },
  'dev-remote': {
    // Server-side can use non-public env vars, client-side uses hardcoded defaults
    apiUrl: isServerSide
      ? process.env.DEV_API_URL || 'https://api.vale.rip'
      : 'https://api.vale.rip',
    authUrl: isServerSide
      ? process.env.DEV_AUTH_URL || 'https://auth.vale.rip'
      : 'https://auth.vale.rip',
    authCookieName: 'vale-auth-token',
    authCookieOptions: {
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      path: '/',
      maxAge: 60 * 60 * 24 * 7 // 7 days
    }
  },
  staging: {
    apiUrl: process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || null,
    authUrl: process.env.NEXT_PUBLIC_AUTH_URL || process.env.AUTH_URL || null,
    authCookieName: 'vale-auth-token',
    authCookieOptions: {
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      path: '/',
      maxAge: 60 * 60 * 24 * 7 // 7 days
    }
  },
  production: {
    apiUrl: process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || null,
    authUrl: process.env.NEXT_PUBLIC_AUTH_URL || process.env.AUTH_URL || null,
    authCookieName: 'vale-auth-token',
    authCookieOptions: {
      httpOnly: true,
      secure: true,
      sameSite: 'lax', // Allow magic links to work across domains
      path: '/',
      maxAge: 60 * 60 * 24 * 7 // 7 days
    }
  }
};

function getEnvironment(): Environment {
  // Check for explicit environment variable (public or private)
  const envVar = process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.ENVIRONMENT;
  if (envVar && envVar in configs) {
    return envVar as Environment;
  }

  // Check if we should use the dev-remote environment
  // Support both server-side and client-side env vars
  const useDevEnv =
    process.env.USE_DEV_ENVIRONMENT === 'true' ||
    process.env.NEXT_PUBLIC_USE_DEV_ENVIRONMENT === 'true' ||
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev-remote';

  if (useDevEnv) {
    return 'dev-remote';
  }

  // Detect based on deployment context
  if (process.env.VERCEL_ENV === 'production') {
    return 'production';
  }
  if (process.env.VERCEL_ENV === 'preview') {
    return 'staging';
  }

  // Default to NODE_ENV
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
}

export const currentEnvironment = getEnvironment();
export const environmentConfig = configs[currentEnvironment];

/**
 * Helper to get the API endpoint URL
 * Throws error in production/staging if API URL is not configured
 */
export function getApiUrl(): string | null {
  // Production/staging must have API URL configured
  if (currentEnvironment === 'production' || currentEnvironment === 'staging') {
    if (!environmentConfig.apiUrl) {
      throw new Error(
        `API URL is required in ${currentEnvironment} environment. Please set NEXT_PUBLIC_API_URL or API_URL`
      );
    }
    return environmentConfig.apiUrl;
  }

  return environmentConfig.apiUrl;
}

/**
 * Helper to get the auth endpoint URL
 * Throws error in production/staging if auth URL is not configured
 */
export function getAuthUrl(): string {
  // Production/staging must have auth URL configured
  if (currentEnvironment === 'production' || currentEnvironment === 'staging') {
    if (!environmentConfig.authUrl) {
      throw new Error(
        `Auth URL is required in ${currentEnvironment} environment. Please set NEXT_PUBLIC_AUTH_URL or AUTH_URL`
      );
    }
    return environmentConfig.authUrl;
  }

  // Development has a default fallback
  return environmentConfig.authUrl || 'https://auth.vale.rip';
}

/**
 * Check if running on a Vercel preview deployment.
 * Preview deployments need special auth handling via auth-proxy.vale.rip.
 */
export function isVercelPreviewDeployment(): boolean {
  // Server-side check
  if (process.env.VERCEL_ENV === 'preview') {
    return true;
  }

  // Client-side check (for dynamic detection)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Preview branches are *.vercel.app but NOT the main deployment
    return (
      hostname.endsWith('.vercel.app') &&
      !hostname.startsWith('law-firms-frontend.')
    );
  }

  return false;
}
